'use client'
// =============================================================================
// HumanMissionsCard — le Challenge devient les missions humaines (accueil).
//
// Poste de pilotage usager : tout ce que Skynet et l'équipe attendent de MOI,
// en un seul endroit, gamifié (rôle, XP) —
//   1. Demandes de mission (WAITING_USER) : questions/choix à répondre,
//      tâches manuelles à accomplir (instructions détaillées, ⭐ XP)
//   2. Tâches bureau qui me sont attribuées (modules/tasks) :
//      ▶ mettre en cours · ✓ terminer
// Aucun élément en attente → fallback (QuizCard : le challenge-jeu).
// =============================================================================
import { useCallback, useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'
import { isAdminUser } from '@/lib/is-admin'

const AMBER = '#f5a623'
const GREEN = '#34d399'
const CYAN  = '#00d4ff'
const RED   = '#ef4444'
const MUTED = '#475569'

interface WaitingMission {
  id: string; objective: string; userQuestion?: string | null; userOptions: string[]
  waitingSince?: string | null
  context?: { humanRole?: string; humanInstructions?: string; humanXp?: number; humanCause?: string } | null
}
interface MyTask {
  id: string; title: string; status: string; priority: string
  dueDate?: string | null
  bureau?: { name?: string } | null
}

const ROLE_LABEL: Record<string, string> = { SUPER_ADMIN: '👑', ADMIN: '🛡', USER: '👤' }

export function HumanMissionsCard() {
  const { data: session } = useSession()
  const uid     = (session?.user as { id?: string } | undefined)?.id
  const isAdmin = isAdminUser(session?.user as never)

  const [missions, setMissions] = useState<WaitingMission[]>([])
  const [tasks, setTasks]       = useState<MyTask[]>([])
  const [busy, setBusy]         = useState<string | null>(null)
  const [xpFlash, setXpFlash]   = useState<string | null>(null)
  const [free, setFree]         = useState<Record<string, string>>({})

  const refresh = useCallback(() => {
    fetch(`/api/missions?status=WAITING_USER&limit=20${isAdmin ? '&all=1' : ''}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => setMissions(Array.isArray(d) ? d : []))
      .catch(() => setMissions([]))
    if (uid) {
      fetch(`/api/tasks?assigneeId=${encodeURIComponent(uid)}&limit=30`)
        .then(r => (r.ok ? r.json() : null))
        .then(d => {
          const items: MyTask[] = d?.items ?? d?.tasks ?? (Array.isArray(d) ? d : [])
          setTasks(items.filter(t => t.status === 'TODO' || t.status === 'IN_PROGRESS'))
        })
        .catch(() => setTasks([]))
    }
  }, [uid, isAdmin])

  useEffect(() => {
    refresh()
    const iv = setInterval(refresh, 60_000)
    return () => clearInterval(iv)
  }, [refresh])

  async function answer(missionId: string, text: string) {
    if (!text.trim() || busy) return
    setBusy(missionId)
    try {
      const r = await fetch(`/api/missions/${missionId}/answer`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answer: text.trim() }),
      })
      if (r.ok) {
        const d = await r.json() as { xpAward?: { earned: number; totalXp: number } | null }
        if (d.xpAward?.earned) setXpFlash(`⭐ +${d.xpAward.earned} XP — total ${d.xpAward.totalXp}`)
      }
    } finally { setBusy(null); refresh() }
  }

  async function setTaskStatus(taskId: string, status: 'IN_PROGRESS' | 'DONE') {
    if (busy) return
    setBusy(taskId)
    try {
      await fetch(`/api/tasks/${taskId}/status`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      })
    } finally { setBusy(null); refresh() }
  }

  const total = missions.length + tasks.length

  return (
    <div className="hud-card" style={{ padding: '18px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: '#e2e8f0' }}>🎯 Vos missions humaines</span>
        <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: total ? AMBER : MUTED, background: `${total ? AMBER : MUTED}14`, padding: '2px 8px', borderRadius: 10, fontWeight: 700 }}>
          {total ? `${total} en attente` : 'à jour ✓'}
        </span>
        {xpFlash && <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: GREEN, fontWeight: 800 }}>{xpFlash}</span>}
      </div>

      {total === 0 && (
        <div style={{ fontSize: 10, color: MUTED, fontFamily: 'var(--font-mono)', lineHeight: 1.7, marginBottom: 10 }}>
          Aucune mission ne vous attend — Skynet travaille. Les demandes (questions, choix, tâches manuelles ⭐ XP) apparaîtront ici.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {missions.map(m => {
          const role       = m.context?.humanRole
          const needsAdmin = role === 'ADMIN' || role === 'SUPER_ADMIN'
          const canAct     = !needsAdmin || isAdmin
          return (
            <div key={m.id} style={{ padding: '10px 12px', borderRadius: 9, background: `${AMBER}08`, border: `1px solid ${AMBER}30` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#e2e8f0', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {m.objective}
                {role && <span style={{ fontSize: 9, color: needsAdmin ? RED : CYAN }}>{ROLE_LABEL[role] ?? ''} {role}</span>}
                {(m.context?.humanXp ?? 0) > 0 && <span style={{ fontSize: 9, color: GREEN, fontWeight: 800 }}>⭐ +{m.context!.humanXp} XP</span>}
              </div>
              {/* Revue d'entrée : la question brute n'est que le code de la capacité (ex. « sms.send ») — on l'explique,
                  la décision complète est dans /bureau/tasks/validations. */}
              {m.userQuestion && (
                <div style={{ fontSize: 11, color: '#fbd38d', marginTop: 4, lineHeight: 1.5 }}>
                  {m.context?.humanCause === 'INTAKE_REVIEW'
                    ? <>🛬 Proposition à revoir avant d’entrer dans le pipeline — signal : <code>{m.userQuestion}</code>. <a href="/bureau/tasks/validations" style={{ color: CYAN }}>Voir la décision →</a></>
                    : <>❓ {m.userQuestion}</>}
                </div>
              )}
              {m.context?.humanInstructions && (
                <div style={{ marginTop: 6, padding: '7px 9px', background: 'rgba(0,0,0,0.25)', borderRadius: 6, fontSize: 10, color: '#cbd5e1', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  📋 {m.context.humanInstructions}
                </div>
              )}
              {!canAct ? (
                <div style={{ marginTop: 6, fontSize: 9, color: MUTED, fontFamily: 'var(--font-mono)' }}>🔒 Réservée à l'administrateur</div>
              ) : m.userOptions.length > 0 ? (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                  {m.userOptions.map(opt => (
                    <button key={opt} onClick={() => answer(m.id, opt)} disabled={busy === m.id}
                      style={{ padding: '4px 12px', background: `${AMBER}18`, border: `1px solid ${AMBER}45`, borderRadius: 7, color: AMBER, cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>
                      {opt}
                    </button>
                  ))}
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <input value={free[m.id] ?? ''} onChange={e => setFree(f => ({ ...f, [m.id]: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') answer(m.id, free[m.id] ?? '') }}
                    placeholder={m.context?.humanInstructions ? 'Décrivez ce qui a été accompli…' : 'Votre réponse…'}
                    style={{ flex: 1, padding: '5px 9px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 7, color: '#e2e8f0', fontSize: 10, outline: 'none' }} />
                  <button onClick={() => answer(m.id, free[m.id] ?? '')} disabled={busy === m.id || !(free[m.id] ?? '').trim()}
                    style={{ padding: '5px 12px', background: AMBER, border: 'none', borderRadius: 7, color: '#0a0d16', cursor: 'pointer', fontSize: 10, fontWeight: 800 }}>
                    {m.context?.humanInstructions ? '✔ Accompli' : 'Répondre'}
                  </button>
                </div>
              )}
            </div>
          )
        })}

        {tasks.map(t => (
          <div key={t.id} style={{ padding: '9px 12px', borderRadius: 9, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: 150, fontSize: 11, fontWeight: 600, color: '#e2e8f0' }}>
              🗂 {t.title}
              {t.bureau?.name && <span style={{ fontSize: 9, color: MUTED, marginLeft: 6 }}>· {t.bureau.name}</span>}
            </span>
            <span style={{ fontSize: 8, fontFamily: 'var(--font-mono)', fontWeight: 700, color: t.status === 'IN_PROGRESS' ? CYAN : MUTED }}>
              {t.status === 'IN_PROGRESS' ? '⚙ EN COURS' : '⧗ À FAIRE'}
            </span>
            {t.status === 'TODO' && (
              <button onClick={() => setTaskStatus(t.id, 'IN_PROGRESS')} disabled={busy === t.id}
                style={{ padding: '3px 10px', background: `${CYAN}14`, border: `1px solid ${CYAN}40`, borderRadius: 6, color: CYAN, cursor: 'pointer', fontSize: 9, fontWeight: 700 }}>
                ▶ Mettre en cours
              </button>
            )}
            <button onClick={() => setTaskStatus(t.id, 'DONE')} disabled={busy === t.id}
              style={{ padding: '3px 10px', background: `${GREEN}14`, border: `1px solid ${GREEN}40`, borderRadius: 6, color: GREEN, cursor: 'pointer', fontSize: 9, fontWeight: 700 }}>
              ✓ Terminer
            </button>
          </div>
        ))}
      </div>

      {/* Outils — chaque nouvelle page du système atterrit ici (surface usager) */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        {[
          { href: '/projets',            label: '🧠 Journal Autonome' },
          { href: '/technologue/villes', label: '🏛 Découverte villes' },
          { href: '/technologue/graphe', label: '🕸 Graphe (enrichir)' },
          { href: '/technologue/carte',  label: '🗺 Carte' },
          { href: '/permis',             label: '📄 Permis' },
        ].map(l => (
          <a key={l.href} href={l.href}
            style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: '#94a3b8', textDecoration: 'none', padding: '3px 9px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 7 }}>
            {l.label}
          </a>
        ))}
      </div>
    </div>
  )
}
